import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';

import { Checkbox } from '@eventbrite/eds-checkbox';
import { MoreActions } from '@eventbrite/eds-more-actions';
import { TEXT_ITEMS_PROP_TYPE } from '@eventbrite/eds-text-list';

import * as constants from './constants';

import './dataTableListItem.scss';

const DataTableListItemCheckBox = ({
    ariaLabel,
    id,
    isDisabled,
    isSelectable,
    isSelected,
    onSelect,
}) => {
    if (!isSelectable) {
        return null;
    }

    return (
        <td
            className="eds-data-table-list-item__column"
            data-spec="data-table-list-item-column"
        >
            <Checkbox
                id={`${id}`}
                value={!!isSelected}
                onChange={onSelect}
                isDisabled={!!isDisabled}
                ariaLabel={ariaLabel}
            />
        </td>
    );
};

const _addEditAction = (actions = []) => [constants.EDIT_ACTION, ...actions];

//If any of the columns include an
//`isEditable=true`, return true
const _checkEditableStatus = (columns) =>
    some(columns, ({ isEditable }) => isEditable);

export default class DataTableListItem extends React.PureComponent {
    static propTypes = {
        /**
         * The values for each column, the data. Any keys in the object
         * must match the provided fieldNames in the columns prop.
         * ie. {email: 'foo@bar.com', firstName: 'Bob'}
         **/
        item: constants.ITEM_TYPE.isRequired,
        /**
         * An array of objects which define the
         * column name, label, editability, and sortability.
         * ie. [{fieldName: 'email', fieldLabel: 'Email': isEditable: true, isSortable: false}]
         **/
        columns: constants.COLUMNS_PROP_TYPE.isRequired,
        /**
         * Value to disable the item
         **/
        isDisabled: PropTypes.bool,
        /**
         * Value to hightlight the item
         **/
        isHighlighted: PropTypes.bool,
        /**
         * Determines if this DatatableListItem should have a checkBox rendered
         **/
        isSelectable: PropTypes.bool,
        /**
         * If the DatatableListItem is currently selected
         **/
        isSelected: PropTypes.bool,
        /**
         * The actions (besides edit) that this listItem supports
         **/
        actions: TEXT_ITEMS_PROP_TYPE,
        /**
         * Function that will take the action type (ie. 'edit')
         * and the associated item {email: 'test', id: 1}
         * ie. (actionType, item) => {}
         **/
        onAction: PropTypes.func,
    };

    static defaultProps = {
        isDisabled: false,
        isHighlighted: false,
        isSelected: false,
        isSelectable: false,
    };

    _handleAction(actionType, value) {
        const { onAction, item } = this.props;

        if (onAction) {
            onAction(actionType, item.id, value);
        }
    }

    _getContent(item, { fieldName }) {
        // check for custom display field, then fallback to field content
        return (
            item[`${fieldName}${constants.CUSTOM_PROPERTY_DISPLAY_SUFFIX}`] ||
            item[fieldName]
        );
    }

    _getColumns(item, columns, isWideColumn) {
        return columns.map(({ fieldName, isClickable }) => {
            const content = this._getContent(item, { fieldName });

            let clickableProps = {};

            if (isClickable) {
                clickableProps = {
                    onClick: this._handleAction.bind(
                        this,
                        constants.CLICK_ACTIONTYPE,
                        fieldName,
                    ),
                    tabIndex: 0,
                    role: 'button',
                };
            }
            const columnClassName = isWideColumn
                ? 'eds-data-table-list-item__wide-column'
                : 'eds-data-table-list-item__column';

            return (
                <td
                    {...clickableProps}
                    className={columnClassName}
                    key={fieldName}
                    data-spec="data-table-list-item-column"
                >
                    {content}
                </td>
            );
        });
    }

    _generateActions(columns) {
        const { isDisabled } = this.props;
        let { actions } = this.props;

        if (_checkEditableStatus(columns)) {
            actions = _addEditAction(actions);
        }

        if (isEmpty(actions)) {
            return null;
        }

        return (
            <td
                className="eds-data-table-list-item__column"
                data-spec="data-table-list-item-column"
            >
                <MoreActions
                    items={actions}
                    onAction={this._handleAction.bind(this)}
                    dropdownAlign="right"
                    isDisabled={isDisabled}
                />
            </td>
        );
    }

    _getAriaLabelForColumn(item, column) {
        const content = this._getContent(item, column) || '';

        return content.toString();
    }

    render() {
        const {
            columns,
            item,
            isDisabled,
            isHighlighted,
            isSelectable,
            isSelected,
            isWideColumn,
            noBorderForTableRow,
            isTableListItemHover,
        } = this.props;
        const dataTableListItemActions = this._generateActions(columns);
        const dataTableListItemColumns = this._getColumns(
            item,
            columns,
            isWideColumn,
        );
        const ariaLabelForItemCheckbox = this._getAriaLabelForColumn(
            item,
            columns[0],
        );
        const className = classNames({
            'eds-bg-color--ui-100': isHighlighted,
            'eds-data-table-list-item': !noBorderForTableRow,
            'eds-data-table-list-item__hover': isTableListItemHover,
        });

        return (
            <tr className={className}>
                <DataTableListItemCheckBox
                    ariaLabel={ariaLabelForItemCheckbox}
                    id={item.id}
                    isDisabled={isDisabled}
                    isSelectable={isSelectable}
                    isSelected={isSelected}
                    onSelect={this._handleAction.bind(
                        this,
                        constants.SELECT_ACTIONTYPE,
                    )}
                />

                {dataTableListItemColumns}

                {dataTableListItemActions}
            </tr>
        );
    }
}
